import React from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import ProtectedRoute from 'components/ProtectedRoute';
import { hasPermission, PERMISSION_NAME } from 'v2/utils/permissions';
import { searchViewPath } from 'v2/utils/searchViewUtils';
import { Permission } from 'v2/models';
import ActiveSearch from './Tabs/ActiveSearch';
import InProgress from './Tabs/InProgress';
import InReview from './Tabs/InReview';
import Archived from './Tabs/Archived';
import AllSearches from './Tabs/AllSearches';
import ForReview from './Tabs/ForReview';
import InProgressRedirect from './Redirects/InProgressRedirect';
import ForReviewRedirect from './Redirects/ForReviewRedirect';
import { useFeatureFlagContext } from 'v2/context/featureFlagContext';
import { FEATURE_FLAGS } from 'utils/constants';

interface SearchViewRoutesProps {
  permissions: { [key: string]: Permission };
}

const SearchViewRoutes = ({ permissions }: SearchViewRoutesProps) => {
  const { flags } = useFeatureFlagContext();
  const isFreeConnectEnabled = flags && flags[FEATURE_FLAGS.FREE_CONNECT];
  const getDefaultPath = (): string => {
    if (
      hasPermission(permissions, [
        PERMISSION_NAME.REVIEW_SEARCH_ORG,
        PERMISSION_NAME.REVIEW_SEARCH_MASTER_ORG,
      ])
    ) {
      return searchViewPath.forReview;
    }
    if (
      hasPermission(permissions, [
        PERMISSION_NAME.CREATE_INTERNAL_SEARCH,
        PERMISSION_NAME.CREATE_INTERNAL_SEARCH_OWN,
      ])
    ) {
      return `${searchViewPath.inProgress}`;
    }
    return `${searchViewPath.active}/1`;
  };

  return (
    <Switch>
      <Route exact path={`${searchViewPath.inReview}/nav`}>
        <Redirect to={`${searchViewPath.inReview}/1`} />
      </Route>
      <Route exact path={`${searchViewPath.active}/nav`}>
        <Redirect to={`${searchViewPath.active}/1`} />
      </Route>

      <ProtectedRoute
        path={`${searchViewPath.active}/:page`}
        component={ActiveSearch}
        permissions={[
          PERMISSION_NAME.CREATE_SEARCH,
          PERMISSION_NAME.CREATE_SEARCH_OWN,
          PERMISSION_NAME.CREATE_INTERNAL_SEARCH,
          PERMISSION_NAME.CREATE_INTERNAL_SEARCH_OWN,
          ...(isFreeConnectEnabled ? [PERMISSION_NAME.CREATE_SEARCH_FREE] : []),
        ]}
      />
      <ProtectedRoute
        path={`${searchViewPath.inProgress}/:page`}
        component={InProgress}
        permissions={[
          PERMISSION_NAME.CREATE_SEARCH,
          PERMISSION_NAME.CREATE_INTERNAL_SEARCH,
          PERMISSION_NAME.CREATE_SEARCH_OWN,
          PERMISSION_NAME.CREATE_INTERNAL_SEARCH_OWN,
        ]}
      />
      <ProtectedRoute
        path={searchViewPath.inProgress}
        component={InProgressRedirect}
        permissions={[
          PERMISSION_NAME.CREATE_SEARCH,
          PERMISSION_NAME.CREATE_INTERNAL_SEARCH,
          PERMISSION_NAME.CREATE_SEARCH_OWN,
          PERMISSION_NAME.CREATE_INTERNAL_SEARCH_OWN,
        ]}
      />
      <ProtectedRoute
        path={`${searchViewPath.inReview}/:page`}
        component={InReview}
        permissions={[
          PERMISSION_NAME.CREATE_SEARCH,
          PERMISSION_NAME.CREATE_INTERNAL_SEARCH,
          PERMISSION_NAME.CREATE_SEARCH_OWN,
          PERMISSION_NAME.CREATE_INTERNAL_SEARCH_OWN,
        ]}
      />
      <ProtectedRoute
        path={`${searchViewPath.archived}/:page`}
        component={Archived}
        permissions={[
          PERMISSION_NAME.CREATE_SEARCH,
          PERMISSION_NAME.CREATE_INTERNAL_SEARCH,
          PERMISSION_NAME.CREATE_SEARCH_OWN,
          PERMISSION_NAME.CREATE_INTERNAL_SEARCH_OWN,
        ]}
      />
      <ProtectedRoute
        path={[
          `${searchViewPath.allSearches}/:page`,
          `${searchViewPath.allSearches}/:page/:searchText`,
        ]}
        exact={true}
        component={AllSearches}
        permissions={[
          PERMISSION_NAME.REVIEW_SEARCH_ORG,
          PERMISSION_NAME.REVIEW_SEARCH_MASTER_ORG,
        ]}
      />
      <ProtectedRoute
        path={[`${searchViewPath.forReview}/:tab/:page`]}
        component={ForReview}
        permissions={[
          PERMISSION_NAME.REVIEW_SEARCH_ORG,
          PERMISSION_NAME.REVIEW_SEARCH_MASTER_ORG,
        ]}
      />
      <ProtectedRoute
        path={searchViewPath.forReview}
        component={ForReviewRedirect}
        permissions={[
          PERMISSION_NAME.REVIEW_SEARCH_ORG,
          PERMISSION_NAME.REVIEW_SEARCH_MASTER_ORG,
        ]}
      />

      <Route path={[searchViewPath.searches, `${searchViewPath.searches}/*`]}>
        <Redirect
          to={{
            pathname: getDefaultPath(),
            state: { referrer: searchViewPath.searches },
          }}
        />
      </Route>
    </Switch>
  );
};

export default SearchViewRoutes;
